import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaTrashRestore, FaTrash, FaUniversity, FaExclamationTriangle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import cpApi from '../../utils/cpApi'; // Adjust path if needed
import './RecycleBin.css';

const RecycleBin = () => {
  const [deletedColleges, setDeletedColleges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // ── Load Deleted Data ────────────────────────────────────────────────────────
  const loadDeletedItems = async () => {
    setLoading(true);
    try {
      // NOTE: Adjust this endpoint to match your backend route for fetching deleted colleges
      const { data } = await cpApi.get('/controlpanel/colleges/recycle-bin'); 
      setDeletedColleges(data.data || []);
    } catch (err) {
      console.error('Failed to load recycle bin:', err);
      // Optional: Handle error gracefully without disrupting the UI
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeletedItems();
  }, []);

  // ── Restore Action ───────────────────────────────────────────────────────────
  const handleRestore = (college) => {
    Swal.fire({
      title: 'Restore College?',
      text: `"${college.name}" will be restored and active again.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#27ae60',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, Restore',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          // NOTE: Adjust this endpoint to match your backend restore route
          await cpApi.put(`/controlpanel/colleges/${college._id}/restore`);
          
          setDeletedColleges(prev => prev.filter(c => c._id !== college._id));
          Swal.fire('Restored!', `"${college.name}" has been restored.`, 'success');
        } catch (err) {
          Swal.fire('Error', err.response?.data?.message || 'Failed to restore.', 'error');
        }
      }
    });
  };

  // ── Permanently Delete Action ────────────────────────────────────────────────
  const handlePermanentDelete = async (college) => {
    const { value: confirmText } = await Swal.fire({
      title: '⚠️ PERMANENT DELETE WARNING',
      html: `
        <div style="text-align: left; padding: 10px;">
          <p style="font-weight: bold; color: #e74c3c; margin-bottom: 15px;">
            This action CANNOT be undone!
          </p>
          <p style="margin-bottom: 10px; font-weight: 600;">
            The following data will be PERMANENTLY DELETED:
          </p>
          <ul style="margin: 10px 0; padding-left: 20px; color: #555;">
            <li>College: <strong>${college.name}</strong></li>
            <li>All Departments</li>
            <li>All Students Data</li>
            <li>All Employees Data</li>
            <li>All Fee Records & Payments</li>
            <li>All Salary Records</li>
            <li>All Transport Data</li>
            <li>All Attendance Records</li>
            <li>All Expenses & Expense Heads</li>
            <li>All Holidays</li>
            <li>All Alert Logs</li>
            <li>All Notifications</li>
            <li>All News</li>
            <li>Everything related to this college</li>
          </ul>
          <p style="margin-top: 15px; font-weight: 600; color: #e74c3c;">
            Type the college name to confirm:
          </p>
        </div>
      `,
      input: 'text',
      inputPlaceholder: `Type "${college.name}" to confirm`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Permanently Delete',
      cancelButtonText: 'Cancel',
      inputValidator: (value) => {
        if (!value) {
          return 'You must type the college name to confirm!';
        }
        if (value.trim() !== college.name) {
          return 'College name does not match!';
        }
      }
    });

    if (confirmText && confirmText.trim() === college.name) {
      try {
        await cpApi.delete(`/controlpanel/colleges/${college._id}/force`);
        setDeletedColleges(prev => prev.filter(c => c._id !== college._id));
        Swal.fire({
          title: 'Deleted!',
          text: `"${college.name}" and all related data has been permanently destroyed.`,
          icon: 'success',
          confirmButtonColor: '#27ae60'
        });
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || 'Failed to delete permanently.', 'error');
      }
    }
  };

  // ── Search Filter ────────────────────────────────────────────────────────────
  const displayedColleges = useMemo(() => {
    if (!searchQuery) return deletedColleges;
    const q = searchQuery.toLowerCase();
    return deletedColleges.filter(c => 
      c.name?.toLowerCase().includes(q) || c.code?.toLowerCase().includes(q)
    );
  }, [deletedColleges, searchQuery]);

  return (
    <div className="re-cycle-bin-container">
      <div className="re-cycle-bin-header-section">
        <h2 className="re-cycle-bin-title">
          <FaTrash style={{ marginRight: '10px', color: '#e74c3c' }} />
          Recycle Bin
        </h2>
        <p className="re-cycle-bin-subtitle">Restore or permanently delete removed colleges.</p>
      </div>

      {/* Controls */}
      <div className="re-cycle-bin-controls">
        <div className="re-cycle-bin-search-wrapper">
          <FaSearch className="re-cycle-bin-search-icon" />
          <input
            type="text"
            className="re-cycle-bin-search-input"
            placeholder="Search deleted colleges by name or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table Section */}
      <div className="re-cycle-bin-table-wrapper">
        {loading ? (
          <div className="re-cycle-bin-loading">Loading deleted items...</div>
        ) : (
          <table className="re-cycle-bin-table">
            <thead>
              <tr>
                <th className="re-cycle-bin-col-sno">S.No</th>
                <th className="re-cycle-bin-align-left">College Details</th>
                <th className="re-cycle-bin-col-date">Deleted On</th>
                <th className="re-cycle-bin-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedColleges.length === 0 ? (
                <tr>
                  <td colSpan="4" className="re-cycle-bin-empty-row">
                    <div className="re-cycle-bin-empty-content">
                      <FaTrashRestore className="re-cycle-bin-empty-icon" />
                      <p>Recycle bin is empty.</p>
                      <span>No deleted colleges found.</span>
                    </div>
                  </td>
                </tr>
              ) : (
                displayedColleges.map((college, index) => (
                  <tr key={college._id} className="re-cycle-bin-main-row">
                    
                    <td className="re-cycle-bin-col-sno">{index + 1}</td>
                    
                    <td className="re-cycle-bin-align-left">
                      <div className="re-cycle-bin-info-wrapper">
                        <div className="re-cycle-bin-icon-box">
                          <FaUniversity />
                        </div>
                        <div className="re-cycle-bin-info-text">
                          <span className="re-cycle-bin-name">{college.name}</span>
                          <span className="re-cycle-bin-code">{college.code || 'N/A'}</span>
                        </div>
                      </div>
                    </td>

                    <td className="re-cycle-bin-col-date">
                      <span className="re-cycle-bin-date-badge">
                        {/* Fallback to 'Recently' if deletedAt isn't returned by your backend */}
                        {college.deletedAt ? new Date(college.deletedAt).toLocaleDateString() : 'Recently'}
                      </span>
                    </td>

                    <td className="re-cycle-bin-col-actions">
                      <div className="re-cycle-bin-action-btns">
                        <button 
                          className="re-cycle-bin-btn-restore" 
                          onClick={() => handleRestore(college)}
                          title="Restore College"
                        >
                          <FaTrashRestore /> Restore
                        </button>
                        
                        <button 
                          className="re-cycle-bin-btn-delete" 
                          onClick={() => handlePermanentDelete(college)}
                          title="Delete Permanently"
                        >
                          <FaTrash /> Delete
                        </button>
                      </div>
                    </td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RecycleBin;